/**
 * Server holati ↔ Zustand store. Ishga tushganda `bootstrap()` server holatini store'ga yozadi (server — haqiqat manbai),
 * localStorage nusxasi — tezkor ko'rsatish va API bo'lmaganda ishlash uchun qoladi.
 * Mutatsiyalar ekranlarda o'z joyida API'ga yuboriladi (engine: sessiyalar; Arcade: o'yinlar; SetEditor: kartochkalar ...).
 */
import { api, ApiError, fire, hasToken, login } from './api';
import { getPlatform } from './telegram';
import { LANG, loadWords } from '../data';
import type { Course, UiLang } from '../data/types';
import { useStore, type Avatar, type AvCfg, type DayLog, type FormState, type Mode, type UserSet, type WordState } from '../store';

export const COURSE_CODE: Record<Course, 'en-uz' | 'ru-uz'> = { en: 'en-uz', ru: 'ru-uz' };

/* ---------- server turlari (lexo-api) ---------- */
export interface ApiUser {
  id: number; name: string; first_name: string; ui_lang: UiLang; course: 'en-uz' | 'ru-uz'; mode: Mode | null; center_name: string | null;
  sound: string; day_offset: number; onboarded: boolean;
  survey: { src: string | null; age: string | null; status: string | null; why: string | null; at: string } | null;
  nick: string | null; bio: string; avatar: Avatar | null; frame: string | null;
}
export interface ApiCourse {
  course: string; level: string; daily_goal: number; xp: number; streak: number; longest_streak: number;
  last_day: number | null; new_day: number | null; new_count: number; right: number; wrong: number;
  best_match: number | null; best_t60: number | null; best_meteor: number | null; order: number[]; active_set_id: number | null;
  duels: { win: number; loss: number; draw: number }; best_num: Record<string, number>;
}
export interface ApiSet { id: number; name: string; ids: number[]; created: string }
export interface ApiState {
  user: ApiUser; course: ApiCourse; words: Record<string, WordState>; hard: number[]; sets: ApiSet[]; forms: Record<string, FormState>;
  log: Record<string, { n: number; r: number; xp: number }>; today: number; streak_now: number;
  entitlements: { plan: 'free' | 'pro' | 'max'; library: boolean; expires_at: string | null };
}
export interface FinishResult {
  xp: number; correct: number; total: number; acc: number; n_new: number; n_review: number; goal_done: boolean; wrong_ids: number[];
  words: Record<string, WordState>; course: ApiCourse;
}
export interface GameResult { xp: number; best: number; is_record: boolean; course: ApiCourse }
export interface FormsResult { xp: number; n_new: number; n_review: number; forms: Record<string, FormState>; course: ApiCourse }
/** Reyting va jangda ko'rinadigan profil; rasm — `/v1/avatars/{id}` (photo=true bo'lsa). */
export interface ApiPlayer { id: number; n: string; nick: string | null; avatar?: { t: 'build'; c: AvCfg } | null; photo: boolean; frame: string | null; xp: number }
export interface DuelAns { ok: boolean; ms: number }
export interface DuelSide { player: ApiPlayer | null; ans: DuelAns[] | null; score: number | null }
export interface ApiDuel {
  code: string; mode: 'friend' | 'random'; level: string; words: number[]; role: 'creator' | 'opponent';
  status: 'open' | 'waiting' | 'ready' | 'done'; result?: 'win' | 'draw' | 'loss'; me: DuelSide; opp: DuelSide; created_at: string; xp?: number;
}
export type GameName = 't60' | 'meteor' | 'match' | 'snake' | 'numbers' | 'forms_chain' | 'forms_match' | 'duel_bot';

/** 'unauth' — server 401 (Telegram'siz ochilgan yoki boshqa bot): token yo'q, ilova Gate ekranini ko'rsatadi. */
export type SyncStatus = 'idle' | 'loading' | 'online' | 'offline' | 'unauth';
let status: SyncStatus = 'idle';
export const syncStatus = () => status;
let plan: ApiState['entitlements'] | null = null;
export const entitlements = () => plan;

/** Mahalliy kun raqami (offset'siz) — server `today` bilan farqi offset'ga yoziladi. */
const localDay = () => Math.floor((Date.now() - new Date().getTimezoneOffset() * 60000) / 86400000);

/** Server holatini store'ga yozish (server ustun). */
export function applyState(st: ApiState) {
  const u = st.user, c = st.course;
  const sets: UserSet[] = st.sets.map((s) => ({ id: s.id, name: s.name, ids: s.ids, created: Math.floor(Date.parse(s.created) / 86400000) }));
  const log: Record<number, DayLog> = {};
  for (const [d, l] of Object.entries(st.log)) log[+d] = { n: l.n, r: l.r };
  const w: Record<number, WordState> = {};
  for (const [id, s] of Object.entries(st.words)) w[+id] = s;
  useStore.getState().set({
    name: u.name || u.first_name, ui: u.ui_lang, mode: u.mode, onb: u.onboarded, sound: u.sound, // ism kiritilmasa Telegram ismi
    survey: { src: u.survey?.src ?? undefined, age: u.survey?.age ?? undefined, status: u.survey?.status ?? undefined, why: u.survey?.why ?? undefined,
      center: u.center_name ?? undefined, learn: u.mode ?? undefined, at: u.survey?.at },
    goal: c.daily_goal, xp: c.xp, streak: c.streak, lastDay: c.last_day, newDay: c.new_day, newCount: c.new_count,
    right: c.right, wrong: c.wrong, best: c.best_match, best60: c.best_t60 ?? undefined, bestMet: c.best_meteor ?? undefined,
    order: c.order, activeSet: c.active_set_id, sets, hm: st.hard, w, log,
    offset: st.today - localDay(),
    lvl: c.level || 'A1', nick: u.nick, bio: u.bio ?? '', avatar: u.avatar ?? null, frame: u.frame ?? null,
    bestStreak: c.longest_streak, duels: c.duels ?? { win: 0, loss: 0, draw: 0 }, bestNum: c.best_num ?? {}, fw: st.forms ?? {},
  });
  plan = st.entitlements;
}

/** Kurs holatining qisman yangilanishi (dars yakuni / o'yin javobidan). */
export function applyCourse(c: ApiCourse, words?: Record<string, WordState>) {
  const s = useStore.getState();
  const w = { ...s.w };
  if (words) for (const [id, st] of Object.entries(words)) w[+id] = st;
  s.set({
    xp: c.xp, streak: c.streak, lastDay: c.last_day, newDay: c.new_day, newCount: c.new_count, right: c.right, wrong: c.wrong,
    best: c.best_match, best60: c.best_t60 ?? undefined, bestMet: c.best_meteor ?? undefined, w,
    bestStreak: c.longest_streak, duels: c.duels ?? s.duels, bestNum: c.best_num ?? s.bestNum,
  });
}

/** Ishga tushganda: login (kerak bo'lsa) → /v1/state → store. Xato bo'lsa offline rejim (localStorage holati bilan). */
export async function bootstrap(): Promise<SyncStatus> {
  status = 'loading';
  try {
    const words = loadWords(); // so'z bazasi holat bilan parallel; xato bo'lsa oxirgi nusxa qoladi
    if (!hasToken()) await login();
    const [st] = await Promise.all([api.get<ApiState>(`/v1/state?course=${COURSE_CODE[LANG]}`), words]);
    // Foydalanuvchining serverdagi joriy kursi boshqa bo'lsa, shu kursga o'tkazamiz (klient LANG — manba)
    if (st.user.course !== COURSE_CODE[LANG]) fire(api.patch('/v1/me', { course: COURSE_CODE[LANG] }), 'course');
    applyState(st);
    status = 'online';
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      console.warn('[sync] auth yo\'q (401):', e.message);
      status = 'unauth';
    } else {
      console.warn('[sync] offline rejim:', e instanceof Error ? e.message : e);
      status = 'offline';
    }
  }
  return status;
}

/* ---------- yozuvlar (ekranlardan chaqiriladi) ---------- */
export const sync = {
  /** Fikr va takliflar (Profil) / so'zda xato. 429 — kunlik chegara. */
  feedback: (b: { kind: 'bug' | 'idea' | 'word' | 'other'; text: string; word_id?: number; screen?: string }) =>
    api.post<{ id: number }>('/v1/feedback', { ...b, platform: getPlatform() ?? 'web' }),
  me: (p: Partial<{ name: string; ui_lang: UiLang; course: 'en-uz' | 'ru-uz'; mode: Mode; center_name: string; sound: string; onboarded: boolean; day_offset: number;
    survey: { src?: string; age?: string; status?: string; why?: string }; nick: string; bio: string; avatar: Avatar | null; frame: string }>) => api.patch<{ user: ApiUser }>('/v1/me', p),
  course: (p: Partial<{ daily_goal: number; order: number[]; active_set_id: number; clear_active_set: boolean; level: string }>) => api.patch<ApiCourse>('/v1/course', p),
  reset: () => api.post<ApiState>('/v1/course/reset'),
  skipDay: () => api.post<{ today: number; day_offset: number }>('/v1/dev/skip-day'),
  hard: (id: number, hard: boolean) => api.put(`/v1/hard/${id}`, { hard }),
  session: (b: { type: string; title?: string; new_ids?: number[]; due_ids?: number[]; word_ids?: number[] }) => api.post<{ id: number }>('/v1/sessions', b),
  answer: (sid: number, b: { client_id: string; word_id: number; step: string; ok: boolean }) => api.post(`/v1/sessions/${sid}/answers`, b),
  finish: (sid: number, b: { known: number[]; match?: { secs: number; mistakes: number; timed: boolean } }) => api.post<FinishResult>(`/v1/sessions/${sid}/finish`, b),
  game: (b: { game: GameName; score: number; mistakes?: number; hints?: number; level?: string; result?: 'win' | 'draw' | 'loss' }) => api.post<GameResult>('/v1/games', b),
  forms: (b: { known: string[]; answers: Array<{ id: string; ok: boolean }> }) => api.post<FormsResult>('/v1/forms/finish', b),
  duelCreate: (mode: 'friend' | 'random') => api.post<ApiDuel>('/v1/duels', { mode }),
  duelGet: (code: string) => api.get<ApiDuel>(`/v1/duels/${encodeURIComponent(code)}`),
  duelSubmit: (code: string, answers: DuelAns[]) => api.post<ApiDuel>(`/v1/duels/${encodeURIComponent(code)}/result`, { answers }),
  duels: () => api.get<{ duels: ApiDuel[] }>('/v1/duels'),
  createSet: (name: string, ids: number[]) => api.post<ApiSet>('/v1/sets', { name, ids }),
  updateSet: (id: number, b: { name?: string; ids?: number[] }) => api.patch<ApiSet>(`/v1/sets/${id}`, b),
  deleteSet: (id: number) => api.del(`/v1/sets/${id}`),
  leaderboard: (scope: string) => api.get<{ list: Array<ApiPlayer & { me?: boolean }>; me: number; me_xp: number }>(`/v1/leaderboard?scope=${scope}`),
  invoice: (plan: 'pro' | 'max', months: 1 | 12) => api.post<{ url: string; stars: number }>('/v1/billing/invoice', { plan, months }),
};
export { fire };
